import React, { useEffect, useMemo, useState } from 'react';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { useConflittiOrario, EsitoConflitto } from '../../../../components/Util/conflittiOrario';

const api = new MyApiClient();

function Modal({ onClose, children, width = 460 }) {
    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1100,
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: '#fff', borderRadius: 12,
                    width, maxWidth: 'calc(100vw - 40px)',
                    maxHeight: 'calc(100vh - 60px)', overflowY: 'auto',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                }}
            >
                {children}
            </div>
        </div>
    );
}

// ── Helpers per format datetime locale (Europe/Rome) ─────────────
function isoToInputs(iso) {
    if (!iso) return { date: '', time: '' };
    const d = new Date(iso);
    if (isNaN(d)) return { date: '', time: '' };
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function inputsToIso(date, time) {
    if (!date || !time) return null;
    // Costruzione locale; il backend interpreta come Europe/Rome via Luxon
    const d = new Date(`${date}T${time}:00`);
    if (isNaN(d)) return null;
    return d.toISOString();
}

const CFG = {
    impianto: { color: '#1565C0', titolo: 'Modifica prenotazione impianto', mostraImpianto: true,  mostraStaff: false },
    lezione:  { color: '#FF9800', titolo: 'Modifica lezione',                mostraImpianto: false, mostraStaff: true  },
    corso:    { color: '#00A15D', titolo: 'Modifica occorrenza corso',       mostraImpianto: true,  mostraStaff: true  },
};

export default function ModificaEventoModal({ event, token, onClose, onUpdated }) {
    const type = event.type || 'impianto';
    const cfg = CFG[type] || CFG.impianto;

    const initial = useMemo(() => {
        const start = isoToInputs(event.dataInizio);
        const end = isoToInputs(event.dataFine);
        return {
            date: start.date,
            startTime: start.time,
            endTime: end.time,
            impiantoId: event.impianto?.id || '',
            staffId: event.staffTecnico?.id || '',
        };
    }, [event]);

    const [date, setDate] = useState(initial.date);
    const [startTime, setStartTime] = useState(initial.startTime);
    const [endTime, setEndTime] = useState(initial.endTime);
    const [impiantoId, setImpiantoId] = useState(initial.impiantoId);
    const [staffId, setStaffId] = useState(initial.staffId);

    const [risorse, setRisorse] = useState({ impianti: [], maestri: [], corsi: [] });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token || !date) return;
        api.getCalendarioRisorse(token, date, date).then(res => {
            if (res && !res.error) setRisorse(res);
        });
    }, [token, date]);

    // Conflitti di orario (uguale in tutta l'app): impianto e maestro liberi nel nuovo orario; con «— mantieni —» vale la risorsa attuale
    const impiantoEffettivo = cfg.mostraImpianto ? (impiantoId || event.impianto?.id || '') : '';
    const staffEffettivo = cfg.mostraStaff ? (staffId || event.staffTecnico?.id || '') : '';
    const verifica = useConflittiOrario({
        data: date, inizio: startTime, fine: endTime,
        impiantoId: impiantoEffettivo, staffId: staffEffettivo, escludiId: event.id,
    });

    const handleSave = async () => {
        setError('');
        if (verifica.inCorso || verifica.conflitto) return;

        const newDataInizio = inputsToIso(date, startTime);
        const newDataFine = inputsToIso(date, endTime);
        if (!newDataInizio || !newDataFine) {
            setError('Compila data e orari');
            return;
        }
        if (new Date(newDataFine) <= new Date(newDataInizio)) {
            setError("L'orario di fine deve essere successivo all'orario di inizio");
            return;
        }

        const payload = { newDataInizio, newDataFine };
        if (cfg.mostraImpianto && impiantoId) payload.newImpiantoId = impiantoId;
        if (cfg.mostraStaff    && staffId)    payload.newStaffId    = staffId;

        setSaving(true);
        const res = await api.rescheduleCalendarioEvento(token, event.id, payload);
        setSaving(false);

        if (res && !res.error) {
            onUpdated && onUpdated(res);
            onClose && onClose();
        } else {
            setError(res?.error || 'Errore durante lo spostamento');
        }
    };

    return (
        <Modal onClose={saving ? () => {} : onClose}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: cfg.color }}>{cfg.titolo}</span>
                <button onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#aaa' }}>✕</button>
            </div>

            <div style={{ padding: '16px 20px 4px' }}>
                <Field label="Data">
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
                </Field>

                <div style={{ display: 'flex', gap: 12 }}>
                    <Field label="Ora inizio" style={{ flex: 1 }}>
                        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={inputStyle} />
                    </Field>
                    <Field label="Ora fine" style={{ flex: 1 }}>
                        <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={inputStyle} />
                    </Field>
                </div>

                {cfg.mostraImpianto && (
                    <Field label="Impianto">
                        <select value={impiantoId} onChange={e => setImpiantoId(e.target.value)} style={inputStyle}>
                            <option value="">— mantieni —</option>
                            {(risorse.impianti || []).map(r => (
                                <option key={r.id} value={r.id}>{r.nome}</option>
                            ))}
                        </select>
                        {impiantoEffettivo && <div style={{ marginTop: 6 }}><EsitoConflitto risultato={verifica.impianto} inCorso={verifica.inCorso} nome="Impianto" nascondiOk={verifica.conflitto} /></div>}
                    </Field>
                )}

                {cfg.mostraStaff && (
                    <Field label="Staff tecnico">
                        <select value={staffId} onChange={e => setStaffId(e.target.value)} style={inputStyle}>
                            <option value="">— mantieni —</option>
                            {Array.from(new Map((risorse.maestri || []).map(r => [r.id, r])).values()).map(r => (
                                <option key={r.id} value={r.id}>{r.nomeCoach || r.nome}</option>
                            ))}
                        </select>
                        {staffEffettivo && <div style={{ marginTop: 6 }}><EsitoConflitto risultato={verifica.maestro} inCorso={verifica.inCorso} nome="Maestro" nascondiOk={verifica.conflitto} /></div>}
                    </Field>
                )}

                {error && (
                    <div style={{ background: '#fff0ee', color: '#b71c1c', padding: '10px 12px', borderRadius: 6, fontSize: 13, margin: '6px 0 0' }}>
                        {error}
                    </div>
                )}

                <div style={{ fontSize: 12, color: '#888', marginTop: 12, lineHeight: 1.4 }}>
                    Verrà controllata la disponibilità della risorsa sul nuovo orario. I partecipanti riceveranno una notifica via email.
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '16px 20px' }}>
                <button onClick={onClose} disabled={saving} style={btnSecondary}>Annulla</button>
                <button onClick={handleSave} disabled={saving || verifica.inCorso || verifica.conflitto} style={{ ...btnPrimary, background: cfg.color }}>
                    {saving ? 'Salvataggio…' : 'Salva modifiche'}
                </button>
            </div>
        </Modal>
    );
}

function Field({ label, children, style }) {
    return (
        <div style={{ marginBottom: 12, ...style }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 4 }}>{label}</div>
            {children}
        </div>
    );
}

const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13,
    border: '1px solid #e0e0e0', borderRadius: 6, outline: 'none',
};
const btnSecondary = {
    flex: 1, padding: '10px', background: '#fff', color: '#333',
    border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const btnPrimary = {
    flex: 1, padding: '10px', color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
};
